/**
 * Spaces API - List Spaces
 *
 * GET /v1/spaces
 *
 * Returns the authenticated user's spaces (sections of any type + custom
 * community groups) pre-shaped with one uniform visibility model baked in
 * server-side. Self-listing only — no subject/scope view-as params.
 */

import { z } from 'zod'
import { AppVisibilitySchema, SpaceKindSchema, SpaceSectionTypeSchema } from './common'

// ============================================================================
// GET /v1/spaces
// ============================================================================

export const SpaceListRequestSchema = z.object({
  // Listing scope (plan 2026-07-09-005 U1 / R1):
  //   - `self` (default) — the authenticated member's own spaces, visibility
  //     FILTERED, unpaginated. This is the frozen mobile contract (KTD1) — its
  //     output must stay byte-identical.
  //   - `admin` — school-wide management listing (admin-authz gated), visibility
  //     SURFACED (hidden rows kept), cursor-paginated.
  scope: z.enum(['self', 'admin']).default('self'),
  // Opaque keyset cursor for `scope=admin` — the composite `(sectionType, name,
  // publicId)` key of the last row on the previous page. Ignored for `scope=self`
  // (unpaginated). `min(1)` so an empty string is rejected rather than treated as
  // "start".
  cursor: z.string().min(1).optional(),
  // Page size for `scope=admin`. Server clamps to `MAX_LIMIT` (500). Ignored for
  // `scope=self`.
  limit: z.coerce.number().int().positive().max(500).optional(),
  // ── Contact-inquiry filter params (plan U4 wires these; schema fields added
  //    now per U1 so the wire contract is stable). Query-param names — the
  //    underlying `UserGroup.contact_enabled` DB field is NOT the wire name.
  //
  //    A literal string-enum transform (NOT `z.coerce.boolean`, which coerces the
  //    string `'false'` to `true`): only `'true'` enables the branch, so
  //    `?contact_target=false` correctly stays in the default listing.
  // Department contact targets (contact_enabled + show_in_app).
  contact_target: z
    .enum(['true', 'false'])
    .transform(v => v === 'true')
    .optional(),
  // Staff inquiry inbox (group_type = CONTACT_INQUIRY), moderator-scoped.
  inquiries: z
    .enum(['true', 'false'])
    .transform(v => v === 'true')
    .optional()
})

export type SpaceListRequest = z.infer<typeof SpaceListRequestSchema>

export const SpaceListItemSchema = z.object({
  id: z.string(),
  kind: SpaceKindSchema,
  // Present on section rows; absent on community rows.
  sectionType: SpaceSectionTypeSchema.optional(),
  name: z.string(),
  // The space avatar (avatarImageUrl). Section.avatarImageUrl for sections,
  // UserGroup.avatarImageUrl for community groups. Nullable when unset.
  icon: z.string().nullable(),
  // Stored 3-state override (for manage UI + web badge later). MANAGER-ONLY on
  // the wire (plan 2026-06-25-001 U8c / R9 / KTD4): the backend emits this raw
  // enum ONLY when the viewer can manage the row (leader/admin). For a pure
  // non-managing member it is OMITTED so a member never learns a space's
  // admin-set hidden/shown status. Clients treat absent as "visible" (the
  // server only ever returns a non-visible row to a manager).
  appVisibility: AppVisibilitySchema.optional(),
  // Resolved Layer-2 result: override or read-time auto-rule. MANAGER-ONLY on
  // the wire, same rule as `appVisibility` above — omitted for pure members.
  effectiveVisible: z.boolean().optional(),
  // Display hint (== isLed); NOT a write gate. Writes still go through
  // requireSectionManager / requireGroupManager.
  canManage: z.boolean(),
  // Community live led row with chatEnabled === false.
  needsSetup: z.boolean(),
  schoolYear: z.string().nullable(),
  // Whether the space's school year is the school's current academic year.
  // Custom community groups are always current.
  isCurrent: z.boolean(),
  membership: z.object({
    role: z.string()
  }),
  // publicIds of the viewer's children whose STUDENT membership drives this
  // section (U9 / R12). Lets a guardian filter/group their Spaces "by child"
  // without a second fetch — the client labels each child id off its already-
  // loaded relationships. Empty on community / parent-level rows and for
  // non-guardian viewers. Two children in the same section → length 2.
  associatedChildIds: z.array(z.string()),
  // Community rows only. Section chat icon is sourced from group-detail (SCH-180),
  // so section rows omit these.
  streamChatChannelId: z.string().nullable().optional(),
  streamChatStatus: z.string().nullable().optional(),
  // Member count for the management list (plan U1). Surfaced by `scope=admin`
  // (from the group/section count); absent on `scope=self` rows.
  memberCount: z.number().optional(),
  // ── Phase-2 Stage-1 additive enablers (SCH-688; plan 2026-07-10-001) ──────
  // The viewer's postability for this space. As of plan 2026-07-16-001 (U2/U4,
  // `withCanPost` in the spaces list use-case) this is resolver-computed and stamped
  // on EVERY row — sections included — not just community rows, so it is now present
  // wherever the list is produced (the mobile-api-test `reads` suite asserts it is
  // present-and-boolean on every row; absent must never be a passing state). The
  // mobile publish picker still sources sections from section-memberships, so it only
  // consumes `canPost` on community rows. Kept `.optional()` for parse-resilience
  // against an old/skewed backend; read it fail-closed (`canPost === true`, never
  // `?? true`) — an absent flag means "unknown / stale backend", not "may post".
  canPost: z.boolean().optional(),
  // Present on `contact_target=true` rows (a department a parent may message).
  // `true` for every row returned by that filter (the filter is
  // contact_enabled + show_in_app); absent on the normal self/admin listing.
  contactEnabled: z.boolean().optional(),
  // The section's school level / division, sourced ONLY from the canonical FK
  // `Section.schoolLevel` (→ `z_RefCoreSchoolLevels`). The legacy denormalized
  // `Section.platformSchoolLevelName` is deliberately NOT a fallback here — it is
  // inert sync metadata, never a linkage (see the legacy-linkage removal plan).
  //
  // ADMIN SCOPE ONLY: stamped on `scope=admin` section rows so the management
  // list can facet by division. Absent on `scope=self` (the frozen mobile
  // contract, KTD1) and on community rows, which carry no level of their own.
  // `null` = a section whose BB offering never resolved to a level row; the UI
  // buckets those under "No school level".
  schoolLevel: z
    .object({
      id: z.string(),
      name: z.string().nullable(),
      abbreviation: z.string().nullable()
    })
    .nullable()
    .optional(),
  // The term this section row is listed FOR (current-term Spaces default).
  // `GET /v1/spaces` no longer lists every section in the school
  // year at once — a section that carries a term is shown only while that term is
  // running, and between terms the list falls forward to the nearest upcoming
  // term. This field names the term the row was kept for so the client can label
  // a fall-forward row ("Fall 2026") instead of silently showing next semester's
  // classes as if they were today's.
  //
  // `isCurrentTerm: false` means the row is a fall-forward one — the term has not
  // started yet. Absent on community rows, on sections that carry no term at all
  // (which are never term-gated), and on `scope=admin` rows (the management list
  // is not term-gated — an admin manages every term's spaces).
  term: z
    .object({
      id: z.string(),
      // Term label as synced (e.g. "1st Semester"); null when BB left it blank.
      description: z.string().nullable(),
      beginDate: z.string().nullable(),
      endDate: z.string().nullable(),
      isCurrentTerm: z.boolean()
    })
    .optional(),
  // The group publicId to open a CONTACT_INQUIRY against (== this row's `id`).
  // Surfaced explicitly on `contact_target=true` rows so the mobile contact UI
  // has a stable target field independent of the list-row identity. Absent on
  // the normal self/admin listing.
  contactTargetGroupId: z.string().optional()
})

export type SpaceListItem = z.infer<typeof SpaceListItemSchema>

export const SpaceListResponseSchema = z.object({
  data: z.array(SpaceListItemSchema),
  message: z.string(),
  // Pagination envelope for `scope=admin` (plan U1 / KTD2). Optional + additive
  // so `scope=self` responses (which omit `meta`) stay valid. `nextCursor` is
  // null at exhaustion.
  meta: z
    .object({
      nextCursor: z.string().nullable()
    })
    .optional()
})

export type SpaceListResponse = z.infer<typeof SpaceListResponseSchema>
